import React, { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { required } from '@hozana/form/validate'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue } from '@hozana/router/functions'
import { bindRoutineToHookForm } from '@hozana/routines/functions'
import { QA } from '@hozana/tests/constants'

import { Button } from 'elements/button/Button'
import { Captcha } from 'elements/input/Captcha'
import { Form } from 'elements/input/Form'
import { Input } from 'elements/input/Input'
import { InputGroup } from 'elements/input/InputGroup'
import { Textarea } from 'elements/input/Textarea'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { Space } from 'elements/layout/Space'
import { P } from 'elements/text/P'
import { Alert } from 'elements/ui/Alert'

import { contactFormSubmit } from 'modules/home/routines'
import { useUser } from 'modules/user/hooks/useUser'

export type TContactFormFormData = {
  email: string
  firstname: string
  lastname: string
  subject: string
  message: string
  captcha: string
}

export const ContactForm: TFC = () => {
  const {
    i18n: { language },
    t,
  } = useTranslation()
  const dispatch = useDispatch()
  const { email, notificationEmail } = useUser()
  const { query } = useRouter()

  const { message, subject } = getFirstQueryValue(query)

  const form = useForm<TContactFormFormData>({
    values: {
      email: notificationEmail ?? email,
      subject,
      message,
      captcha: undefined,
      firstname: undefined,
      lastname: undefined,
    },
    resetOptions: { keepDirtyValues: true },
  })
  const { handleSubmit, setError, clearErrors, setValue, formState, reset } = form

  const onSubmit: SubmitHandler<TContactFormFormData> = bindRoutineToHookForm(contactFormSubmit, {
    setError,
    dispatch,
    onSuccess: reset,
    onFailure: () => {
      // Clear captcha to allow to send the form again
      setValue('captcha', null)
      clearErrors('captcha')
    },
  })

  useEffect(() => {
    if (formState.errors) {
      setValue('captcha', null)
    }
  }, [formState.errors, setValue])

  return (
    <Form id="ContactForm" form={form} onSubmit={handleSubmit(onSubmit)}>
      <InputGroup label="trans:home:home.contact.form.email" name="email" options={required()}>
        <Input
          data-testid={QA.CONTACT_PAGE.EMAIL_INPUT}
          type="email"
          placeholder="trans:home:home.contact.form.email"
        />
      </InputGroup>

      <Row gutter="5px">
        <Cell w={{ xs: 1, sm: 1 / 2 }}>
          <InputGroup label="trans:home:word.your-firstname" name="firstname" options={required()}>
            <Input data-testid={QA.CONTACT_PAGE.FIRSTNAME_INPUT} placeholder="trans:home:word.your-firstname" />
          </InputGroup>
        </Cell>
        <Cell w={{ xs: 1, sm: 1 / 2 }}>
          <InputGroup label="trans:home:home.contact.form.lastname" name="lastname" options={required()}>
            <Input data-testid={QA.CONTACT_PAGE.NAME_INPUT} placeholder="trans:home:home.contact.form.lastname" />
          </InputGroup>
        </Cell>
      </Row>
      <InputGroup label="trans:home:home.contact.form.subject" name="subject" options={required()}>
        <Input data-testid={QA.CONTACT_PAGE.SUBJET_INPUT} placeholder="trans:home:home.contact.form.subject" />
      </InputGroup>
      <Space />
      <Alert status="info">
        <P>trans:home:home.contact.form.please-add-details</P>
      </Alert>
      <InputGroup label="trans:home:home.contact.form.message" name="message" options={required()}>
        <Textarea data-testid={QA.CONTACT_PAGE.MESSAGE_INPUT} placeholder={t('home:home.contact.form.message')} />
      </InputGroup>
      <Div m="1em 0" align="middleCenter">
        <Captcha onChange={(value) => setValue('captcha', value.toString())} lang={language} />
      </Div>
      <Div align="center">
        <Button data-testid={QA.CONTACT_PAGE.SUBMIT_BUTTON} disabled={formState.isSubmitting} type="submit">
          trans:home:home.contact.form.send
        </Button>
      </Div>
    </Form>
  )
}
